/**
 * mergeIncomingMetadata — how a peer's `metadata_updated` lands on our record.
 *
 * MERGE, not replace. `metadata_updated` means "here is what changed", and the
 * two sides do not carry identical records: the venue sheet's push is built
 * from the HOST's metadata, so any field only our side holds (the scan-time
 * `meetingDate` is the one that matters — it is what the WE-MET card and the
 * moment banner render from) was being dropped on arrival.
 *
 * It was safe until now only because `qrDetection` happens to stamp
 * `meetingDate` on both sides. THREAD's venue sheet is the first surface that
 * pushes metadata on a deliberate tap, mid-exchange, which is exactly when a
 * wiped date would be visible — so the branch that introduces that push is the
 * branch that fixes this.
 *
 * The recovery path (`profile_data_response`, ConnectionStateContext) has
 * always merged; the live handler replaced. Two paths for one record that
 * disagreed — this makes them agree.
 *
 * Incoming wins on every key it carries, so a real edit (a venue change, a
 * corrected place) still overwrites. Nothing in the app deletes a metadata
 * key, so "incoming omits it" always means "the sender never had it", never
 * "the sender cleared it".
 *
 * @param {Object|null} existing - our current record
 * @param {Object|null} incoming - the peer's payload
 * @returns {Object} merged record
 */
export const mergeIncomingMetadata = (existing, incoming) => ({
  ...(existing || {}),
  ...(incoming || {}),
});

/**
 * recoverPeerRecord — rebuild our record from the host's stored messages, in the order
 * they speak: the card reply's record (the exchange), then the latest location update
 * (venue edits, and a re-meet's new meeting), each merged over the last. One result,
 * so the caller saves once and files a meeting once — comparing the FINAL record with
 * what this phone held before recovery. (Filing at each step re-filed the first
 * meeting on every open after a re-meet, since the stored card reply keeps the first
 * meeting's key — b/meetings-reconnect review.)
 *
 * @param {Object|null} local - our record before recovery
 * @param {Object|null} replyRecord - the latest card reply's connectionMetadata
 * @param {Object|null} updateRecord - the latest location update's connectionMetadata
 * @returns {Object|null} the recovered record, or null when no message carried one
 */
export const recoverPeerRecord = (local, replyRecord, updateRecord) => {
  if (!replyRecord && !updateRecord) return null;
  let record = local;
  if (replyRecord) record = mergeIncomingMetadata(record, replyRecord);
  if (updateRecord) record = mergeIncomingMetadata(record, updateRecord);
  return record;
};
